import styled from 'styled-components';
import iconMixin from '../../../styles/utils/iconMixin';
import React, { ReactElement } from 'react';
import { getSalesOrderStatusIcon } from '../../../utils/SalesUtils';
import titleCase from '../../../utils/TitleCase';
import { p4Tokens } from '../../../styles/themes/default/p4FluentTheme';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import { PurchaseOrderLineStatus, SalesOrderLineStatus } from '../../../graphql/schema/p4types';
import fieldMetaData from '../../../graphql/schema/fieldMetaData';
import { HeaderStatusIcon, StatusStyle } from '../../connectedForm/HeaderStatus';

export const Badge = styled.div`
  background-color: white;
  border: 1px solid #cfd8dc;
  border-radius: 3px;
  color: #484644;
  display: inline-block;
  font-size: 10px;
  padding: 2px 5px 2px 3px;
  text-transform: uppercase;
`;

export const Icon = styled.div`
  ${iconMixin('14px')};
  padding-right: 5px;
  float: left;
`;

//TODO - Consolidate with 'HeaderStatus' implementation - Why do we invert the background/foreground between these 2 types?
const useStatusWorkspaceStyles = makeStyles({
  statusFlagBasic: { color: p4Tokens.statusFlagBasic },
  statusFlagDanger: { color: p4Tokens.statusFlagDanger },
  statusFlagSuccess: { color: p4Tokens.statusFlagSuccess },
  statusFlagWarning: { color: p4Tokens.statusFlagWarning },
  statusFlagInfo: { color: p4Tokens.statusFlagInfo },
  statusFlagReady: { color: p4Tokens.statusFlagReady },
});

const useGetStatusClassname = () => {
  const styles = useStatusWorkspaceStyles();
  return (statusStyle?: StatusStyle) => {
    switch (statusStyle as StatusStyle & 'error') {
      case 'error':
      case StatusStyle.Danger:
        return styles.statusFlagDanger;
      case StatusStyle.Success:
        return styles.statusFlagSuccess;
      case StatusStyle.Warning:
        return styles.statusFlagWarning;
      case StatusStyle.Info:
        return styles.statusFlagInfo;
      case StatusStyle.Ready:
        return styles.statusFlagReady;
      default:
        return styles.statusFlagBasic;
    }
  };
};

type CommonStatus = SalesOrderLineStatus | PurchaseOrderLineStatus;

const getDisplayValue = (status: CommonStatus) => {
  const statusEnumValues = [
    ...fieldMetaData.SalesOrderLineStatus.enumValues,
    ...fieldMetaData.PurchaseOrderLineStatus.enumValues,
  ];
  const statusEnum = statusEnumValues.find((enumValue) => enumValue.value === status);

  return statusEnum?.label ?? titleCase(status);
};

const WorkspaceStatusBadge = ({ parentKey, value }: { parentKey: string; value: Array<CommonStatus> }) => {
  const getStatusClassname = useGetStatusClassname();

  const badges: Array<ReactElement> = value.map((value) => {
    const { color: statusStyle, type, tooltip } = getSalesOrderStatusIcon(value);
    const iconClassname = getStatusClassname(statusStyle);
    const content = (
      <Badge key={`${parentKey}_${value}`}>
        <HeaderStatusIcon className={iconClassname}>{type}</HeaderStatusIcon>
        {getDisplayValue(value)}
      </Badge>
    );

    if (tooltip) {
      return (
        <Tooltip key={`${parentKey}_${value}_tooltip`} content={tooltip} relationship="description" withArrow={true}>
          {content}
        </Tooltip>
      );
    }
    return content;
  });
  return <>{badges}</>;
};

export default WorkspaceStatusBadge;
